// ----------------------------------------------------------------------
// <copyright file="Izenda.LazyLoadingUIStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import { LAZY_LOADING_DEFAULT_ITEMS_NUMBER } from 'logic/data/Izenda.UIConstants';
import { take, isEmpty, find, isEqual, some, isNil, findIndex, get } from 'lodash/fp';
class LazyLoadingUIStore {
  @observable items = [];
  @observable isLoading = false;
  @observable training = true;

  constructor(defaultData) {
    this.isLastPage = false;
    this.itemHeight = 0;
    this.stop = false;
    this.viewHeight = 0;
    this.pageInfoForFirstTime = {};
    this.virtualNodes = [];
    this.pageSize = defaultData.defaultItemsNumber || 0;
    this.defaultItemsNumber = defaultData.defaultItemsNumber || LAZY_LOADING_DEFAULT_ITEMS_NUMBER;

    this.setupDefaultData(defaultData);
    this.setPageInfoForFirstTime();
  }

  /**
   * get items on screen
   * @returns {Array} items
   */
  @computed
  get getItemsOnViewScreen() {
    return this.isLastPage ? this.items : this.training ? take(1, this.items) : this.items;
  }

  /**
   * Setup default data for lazy loading store
   * @param  {Array} data
   * @param  {Number} viewHeight
   * @param  {Number} itemHeight
   */
  @action('setupDefaultData')
  setupDefaultData(defaultData) {
    if (!isEmpty(defaultData.data)) {
      this.training = false;
      this.viewHeight = defaultData.viewHeight;
      this.itemHeight = defaultData.itemHeight;
      this.items = defaultData.data;
      defaultData.isLastPage && (this.stop = true);
    }
  }

  /**
   * Set view height
   * @param {Number} height view height
   */
  @action('setViewHeight')
  setViewHeight(height) {
    this.viewHeight = height;
  }

  /**
   * update virtual node list
   * @param {Object} source
   * @param {Object} virtualNodeDom
   */
  updateVirtualNodes(source, virtualNodeDom) {
    let virtualNode = find(node => {
      return isEqual(node, source);
    }, this.virtualNodes);

    virtualNode['virtualNodeDom'] = virtualNodeDom;
  }

  /**
   * Set item height
   * @param {Number} itemHeight item height
   */
  setItemHeight(itemHeight) {
    this.itemHeight = itemHeight;
  }

  /**
   * get page information
   * @returns {Object} page information
   */
  getPageInfo(node) {
    let fromIndex, pageSize, parentIds;
    const total = (this.pageSize > 0 || this.itemHeight === 0)
      ? this.pageSize
      : Math.floor(this.viewHeight / this.itemHeight);
    this.pageSize = total;

    if (this.training) {
      fromIndex = 0;
      pageSize = this.defaultItemsNumber;
      parentIds = [];
    } else {
      fromIndex = node.fromIndex;
      pageSize = total;
      parentIds = node.parentIds;
    }

    return {
      fromIndex: fromIndex,
      pageSize: pageSize,
      parentIds: parentIds
    };
  }

  /**
   *
   * @param {*} data
   * @param {*} isLastPage
   */
  setPageInfoForFirstTime() {
    this.pageInfoForFirstTime = {
      fromIndex: 0,
      pageSize: this.defaultItemsNumber,
      parentIds: []
    };
  }

  /**
   * Update data
   * @param {Object} data
   * @param {Boolean} isLastPage
   */
  @action('updateData')
  updateData(data, isLastPage) {
    this.items = data;
    this.isLoading = false;
    this.isLastPage = isLastPage;
    isLastPage && (this.stop = true);
  }

  /**
   * Get virtual node
   * @param {Object} scrollParent
   */
  getVirtualNode(scrollParent) {
    const isRendering = some('isRendering', this.virtualNodes);
    if (isRendering) return;

    const scrollTop = get('scrollTop', scrollParent);

    return find(node => {
      const contHeight = this.viewHeight;
      const contTop = scrollTop;
      const contBottom = contTop + contHeight;

      const elemTop = node.virtualNodeDom.offsetTop;
      return contBottom >= elemTop && elemTop >= contTop && $izenda(node.virtualNodeDom).is(':visible');
    }, this.virtualNodes);
  }

  /**
   * Set rendering node
   * @param {Object} node
   */
  setRenderingNode(node) {
    node.isRendering = true;
  }

  /**
   * Remove virtual node
   */
  @action('removeVirtualNode')
  removeVirtualNode() {
    const renderingNode = find('isRendering', this.virtualNodes);
    if (!isNil(renderingNode)) {
      const renderingNodeIndex = findIndex('isRendering', this.virtualNodes);
      this.virtualNodes.splice(renderingNodeIndex, 1);
    }
  }

  /**
   * Show Loading
   */
  @action('showLoading')
  showLoading() {
    this.isLoading = true;
  }

  /**
   * Reset virtual nodes
   */
  resetVirtualNodes() {
    this.virtualNodes.splice(1, this.virtualNodes.length);
  }
}

export default LazyLoadingUIStore;



// WEBPACK FOOTER //
// ./ui/share/components/controls/LazyLoading/Izenda.LazyLoadingUIStore.js